import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { FireIcon, TrophyIcon, SparklesIcon } from '@heroicons/react/24/solid';
import { getStreakData, getStreakMessage, StreakData } from './streakUtils';
import { useTour } from './TourContext';

const SETTING_KEY = 'daydrive_streak_notification';
const SESSION_SHOWN_KEY = 'daydrive_streak_shown';

export function LoginNotification() {
  const [isVisible, setIsVisible] = useState(false);
  const [streak, setStreak] = useState<StreakData | null>(null);
  const { state: tourState } = useTour();

  useEffect(() => {
    // Don't show streak modal while tour is active or hasn't been completed yet
    if (!tourState.hasCompletedTour || tourState.isActive) return;

    // Only show once per session (per tab/login)
    if (sessionStorage.getItem(SESSION_SHOWN_KEY) === 'true') return;

    // Check if notification is enabled in settings
    const isEnabled = localStorage.getItem(SETTING_KEY) !== 'false';
    if (!isEnabled) return;

    // Get streak data and show notification
    const data = getStreakData();
    setStreak(data);
    setIsVisible(true);
    sessionStorage.setItem(SESSION_SHOWN_KEY, 'true');
  }, [tourState.hasCompletedTour, tourState.isActive]);

  // Handle Escape key to dismiss
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible]);

  const handleDismiss = useCallback(() => {
    setIsVisible(false);
  }, []);

  if (!streak) return null;

  const { currentStreak, longestStreak, isNewDay, totalVisits } = streak;
  const message = getStreakMessage(currentStreak);

  const content = (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
          onClick={handleDismiss}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-sm"
          >
            {/* Gradient border effect */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-orange-500 via-rose-500 to-purple-600 blur-xl opacity-50" />

            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl border border-white/10 overflow-hidden">
              {/* Sparkles decoration */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-conic from-orange-500/20 via-transparent to-purple-500/20"
                />
              </div>

              {/* Close button */}
              <button
                onClick={handleDismiss}
                className="absolute top-3 right-3 p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors z-10"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>

              {/* Content */}
              <div className="relative p-6">
                {/* Welcome header */}
                <div className="text-center mb-5">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                    className="inline-flex items-center gap-1.5 px-3 py-1 bg-indigo-500/20 rounded-full text-indigo-300 text-xs font-medium mb-3"
                  >
                    <SparklesIcon className="w-3.5 h-3.5" />
                    Welcome back!
                  </motion.div>
                </div>

                {/* Streak display */}
                <div className="flex items-center justify-center gap-4 mb-5">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.3, type: 'spring', stiffness: 150 }}
                    className="relative"
                  >
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                      className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 via-rose-500 to-purple-500 flex items-center justify-center shadow-lg shadow-orange-500/30"
                    >
                      <FireIcon className="w-10 h-10 text-white" />
                    </motion.div>
                    {currentStreak > 1 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.5, type: 'spring' }}
                        className="absolute -top-1 -right-1 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center text-sm font-bold text-gray-900 shadow-lg"
                      >
                        {currentStreak > 99 ? '99+' : currentStreak}
                      </motion.div>
                    )}
                  </motion.div>

                  <div className="text-left">
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 }}
                      className="flex items-baseline gap-2"
                    >
                      <span className="text-4xl font-bold text-white">{currentStreak}</span>
                      <span className="text-lg text-gray-400">day{currentStreak > 1 ? 's' : ''}</span>
                    </motion.div>
                    <motion.p
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-sm text-gray-400"
                    >
                      {isNewDay ? 'Streak extended!' : 'Current streak'}
                    </motion.p>
                  </div>
                </div>

                {/* Stats row */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex items-center justify-center gap-4 mb-5"
                >
                  {longestStreak > 1 && (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-500/10 rounded-lg">
                      <TrophyIcon className="w-4 h-4 text-yellow-400" />
                      <span className="text-xs text-yellow-300">Best: {longestStreak} days</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 rounded-lg">
                    <span className="text-xs text-blue-300">Total visits: {totalVisits}</span>
                  </div>
                </motion.div>

                {/* Progress bar */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                  className="mb-4"
                >
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 7 }).map((_, i) => (
                      <motion.div
                        key={i}
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ delay: 0.8 + i * 0.05 }}
                        className={`h-2 flex-1 rounded-full origin-left ${
                          i < Math.min(currentStreak, 7)
                            ? 'bg-gradient-to-r from-orange-400 to-rose-500'
                            : 'bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-500 mt-2 text-center">
                    {currentStreak >= 7
                      ? 'Week streak achieved!'
                      : `${7 - Math.min(currentStreak, 7)} more day${7 - Math.min(currentStreak, 7) > 1 ? 's' : ''} for week streak`}
                  </p>
                </motion.div>

                {/* Motivational message */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="text-center"
                >
                  <p className="text-sm text-gray-300 leading-relaxed">{message}</p>
                </motion.div>

                {/* Dismiss hint */}
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.2 }}
                  className="text-[10px] text-gray-600 text-center mt-4"
                >
                  Tap anywhere to dismiss
                </motion.p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
}
